import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard,
  Users,
  Clock,
  Calendar,
  DollarSign,
  LogOut,
  Building2,
  CheckSquare,
  FileText,
  Briefcase,
  ToggleLeft,
  BarChart3,
  CalendarClock,
  ListTodo,
  CalendarDays,
  UsersRound,
  FileEdit,
  Menu,
  X,
  Inbox,
  Palette,
  UserCog
} from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { getInitials } from '../../lib/utils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [branding, setBranding] = useState({ logoUrl: null, appName: 'Workforce OS' });
  
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  
  // Fetch branding on mount and listen for updates
  useEffect(() => {
    fetchBranding();
    
    // Listen for branding updates
    const handleBrandingUpdate = () => {
      fetchBranding();
    };
    window.addEventListener('branding-updated', handleBrandingUpdate);
    window.addEventListener('logo-updated', handleBrandingUpdate); // Keep for backwards compatibility
    
    return () => {
      window.removeEventListener('branding-updated', handleBrandingUpdate);
      window.removeEventListener('logo-updated', handleBrandingUpdate);
    };
  }, []);
  
  const fetchBranding = async () => {
    try {
      const [logoRes, nameRes] = await Promise.all([
        axios.get(`${API_URL}/api/settings/logo`),
        axios.get(`${API_URL}/api/settings/app-name`)
      ]);
      setBranding({
        logoUrl: logoRes.data.has_logo ? logoRes.data.logo_data : null,
        appName: nameRes.data.app_name || 'Workforce OS'
      });
    } catch (error) {
      console.error('Failed to fetch branding:', error);
    }
  };
  
  const ownerNav = [
    { name: 'Portfolio', path: '/app/owner', icon: LayoutDashboard },
    { name: 'Approval Inbox', path: '/app/owner/approval-inbox', icon: Inbox },
    { name: 'Companies', path: '/app/owner/companies', icon: Building2 },
    { name: 'Branding', path: '/app/admin/branding', icon: Palette },
  ];
  
  const adminNav = [
    { name: 'Dashboard', path: '/app/admin', icon: LayoutDashboard },
    { name: 'Employees', path: '/app/admin/employees', icon: Users },
    { name: 'Shifts', path: '/app/admin/shifts', icon: Clock },
    { name: 'Shift Assignment', path: '/app/admin/shift-assignments', icon: CalendarClock },
    { name: 'Holidays', path: '/app/admin/holidays', icon: CalendarDays },
    { name: 'Leave Types', path: '/app/admin/leave-types', icon: Calendar },
    { name: 'Payroll', path: '/app/admin/payroll', icon: DollarSign },
    { name: 'Reports', path: '/app/admin/reports', icon: BarChart3 },
    { name: 'My Team', path: '/app/manager/team', icon: UsersRound },
    { name: 'Features', path: '/app/admin/features', icon: ToggleLeft },
    { name: 'Branding', path: '/app/admin/branding', icon: Palette },
  ];
  
  const managerNav = [
    { name: 'Dashboard', path: '/app/manager', icon: LayoutDashboard },
    { name: 'My Team', path: '/app/manager/team', icon: UsersRound },
    { name: 'Team Attendance', path: '/app/manager/attendance', icon: Clock },
    { name: 'Team Reports', path: '/app/manager/reports', icon: BarChart3 },
    { name: 'Approvals', path: '/app/manager/approvals', icon: CheckSquare },
  ];
  
  // Employee nav items managers can access (excluding payslips)
  const managerEmployeeNav = [
    { name: 'My Attendance', path: '/app/employee/attendance', icon: Clock },
    { name: 'My Tasks', path: '/app/employee/tasks', icon: ListTodo },
    { name: 'My Requests', path: '/app/employee/requests', icon: FileEdit },
    { name: 'My Leaves', path: '/app/employee/leaves', icon: Calendar },
  ];
  
  const employeeNav = [
    { name: 'Dashboard', path: '/app/employee', icon: LayoutDashboard },
    { name: 'Attendance', path: '/app/employee/attendance', icon: Clock },
    { name: 'My Tasks', path: '/app/employee/tasks', icon: ListTodo },
    { name: 'My Requests', path: '/app/employee/requests', icon: FileEdit },
    { name: 'Leaves', path: '/app/employee/leaves', icon: Calendar },
    { name: 'Payslips', path: '/app/employee/payslips', icon: FileText },
  ];
  
  const getNavItems = () => {
    switch (user?.role) {
      case 'owner':
        return ownerNav;
      case 'admin':
        return adminNav;
      case 'manager':
        return [...managerNav, ...managerEmployeeNav];
      default:
        return employeeNav;
    }
  };
  
  const navItems = getNavItems();
  
  const handleNavClick = () => {
    if (onClose) onClose();
  };
  
  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      <aside className={cn(
        "fixed left-0 top-0 z-50 h-screen w-64 border-r border-border bg-white transition-transform duration-300",
        "lg:translate-x-0 lg:z-40",
        isOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-between border-b border-border px-4 lg:px-6">
            <Link to="/" className="flex items-center gap-2">
              {branding.logoUrl ? (
                <img src={branding.logoUrl} alt="Logo" className="h-8 w-8 object-contain" />
              ) : (
                <Briefcase className="h-6 w-6 text-primary" />
              )}
              <span className="text-lg font-bold text-primary" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                {branding.appName}
              </span>
            </Link>
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden"
              onClick={onClose}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 space-y-1 px-3 py-4 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className={cn(
                  'sidebar-item',
                  isActive(item.path) ? 'sidebar-item-active' : 'sidebar-item-inactive'
                )}
                data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.name}</span>
              </Link>
            ))}
          </nav>
          
          {/* User section */}
          <div className="border-t border-border p-4">
            <div className="flex items-center gap-3 mb-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={user?.avatar_url} alt={user?.first_name} />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {getInitials(user?.first_name, user?.last_name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full justify-start gap-2"
              onClick={logout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Mobile header component
export const MobileHeader = ({ onMenuClick }) => {
  const { user } = useAuth();
  const [branding, setBranding] = useState({ logoUrl: null, appName: 'Workforce OS' });
  
  useEffect(() => {
    fetchBranding();
    
    const handleBrandingUpdate = () => {
      fetchBranding();
    };
    window.addEventListener('branding-updated', handleBrandingUpdate);
    window.addEventListener('logo-updated', handleBrandingUpdate);
    
    return () => {
      window.removeEventListener('branding-updated', handleBrandingUpdate);
      window.removeEventListener('logo-updated', handleBrandingUpdate);
    };
  }, []);
  
  const fetchBranding = async () => {
    try {
      const [logoRes, nameRes] = await Promise.all([
        axios.get(`${API_URL}/api/settings/logo`),
        axios.get(`${API_URL}/api/settings/app-name`)
      ]);
      setBranding({
        logoUrl: logoRes.data.has_logo ? logoRes.data.logo_data : null,
        appName: nameRes.data.app_name || 'Workforce OS'
      });
    } catch (error) {
      setBranding({ logoUrl: null, appName: 'Workforce OS' });
    }
  };
  
  // Get short name for mobile (first word or abbreviation)
  const shortName = branding.appName.split(' ')[0].substring(0, 6);
  
  return (
    <header className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-border z-30 lg:hidden">
      <div className="flex items-center justify-between h-full px-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onMenuClick}>
            <Menu className="h-6 w-6" />
          </Button>
          <Link to="/" className="flex items-center gap-2">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt="Logo" className="h-6 w-6 object-contain" />
            ) : (
              <Briefcase className="h-5 w-5 text-primary" />
            )}
            <span className="font-bold text-primary">{shortName}</span>
          </Link>
        </div>
        <Avatar className="h-8 w-8">
          <AvatarImage src={user?.avatar_url} alt={user?.first_name} />
          <AvatarFallback className="bg-primary text-primary-foreground text-xs">
            {getInitials(user?.first_name, user?.last_name)}
          </AvatarFallback>
        </Avatar>
      </div>
    </header>
  );
};

export default Sidebar;
